import React, { useState, Suspense } from 'react';
import { Menu, Close } from '@mui/icons-material';
import { IconButton } from '@mui/material';

const PrivacyPolicy = React.lazy(() => import('./privacy-policy'));
const TermsConditions = React.lazy(() => import('./termsConditions'));
const LaancStatement = React.lazy(() => import('./LaancStatement'));
const B4UFLYDisclaimers = React.lazy(() => import('./B4UFLYDisclaimers'));
const B4UFLYSafetyGuidelines = React.lazy(() => import('./B4UFLYSafetyGuidelines'));

function Resources() {
    const [selectedContent, setSelectedContent] = useState('privacy-policy');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const renderContent = () => {
        switch (selectedContent) {
            case 'privacy-policy':
                return <PrivacyPolicy />;
            case 'terms-conditions':
                return <TermsConditions />;
            case 'laanc-statement':
                return <LaancStatement />;
            case 'b4ufly-disclaimers':
                return <B4UFLYDisclaimers />;
            case 'b4ufly-safety':
                return <B4UFLYSafetyGuidelines />;
            default:
                return <p>Select an option to view content.</p>;
        }
    };

    const openNewTab = () => {
        window.open('https://www.faa.gov/uas/programs_partnerships/data_exchange/privacy_statement', '_blank');
    };

    const handleMenuItemClick = (content) => {
        setSelectedContent(content);
        setMobileMenuOpen(false); // Close mobile menu after selection
    };

    const menuItems = [
        {
            id: 'privacy-policy',
            title: 'Privacy Policy',
            description: 'Our data protection practices'
        },
        {
            id: 'terms-conditions',
            title: 'Terms and Conditions',
            description: 'Service usage terms'
        },
        {
            id: 'laanc-statement',
            title: 'LAANC Statement',
            description: 'LAANC service information'
        },
        {
            id: 'b4ufly-disclaimers',
            title: 'B4UFLY General Disclaimers',
            description: 'B4UFLY service disclaimers'
        },
        {
            id: 'b4ufly-safety',
            title: 'B4UFLY Safety Guidelines',
            description: 'Safety guidelines for B4UFLY'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-gray-50">
            {/* Mobile Header */}
            <div className="md:hidden bg-white border-b border-gray-200 p-4 sticky top-0 z-[1000] shadow-sm">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Resources</h1>
                        <p className="text-sm text-gray-600">Legal & Policies</p>
                    </div>
                    <IconButton
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                        sx={{ color: '#1e3a8a' }}
                    >
                        {mobileMenuOpen ? <Close /> : <Menu />}
                    </IconButton>
                </div>
            </div>

            {/* Mobile Dropdown Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-white border-b border-gray-200 shadow-lg sticky top-0 z-[999] max-h-[60vh] overflow-y-auto">
                    <nav className="p-4 space-y-2">
                        {menuItems.map((item) => (
                            <button
                                key={item.id}
                                className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                                    selectedContent === item.id
                                        ? 'bg-blue-500 text-white shadow-md'
                                        : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                                onClick={() => handleMenuItemClick(item.id)}
                            >
                                <span className="font-medium text-sm">{item.title}</span>
                                <p className="text-xs mt-1 opacity-80">{item.description}</p>
                            </button>
                        ))}
                        <button
                            className="w-full text-left p-3 rounded-lg text-gray-700 hover:bg-gray-100 border border-gray-200 transition-all duration-200"
                            onClick={openNewTab}
                        >
                            <span className="font-medium text-sm">FAA Privacy Policy</span>
                            <p className="text-xs mt-1 opacity-80">External FAA documentation</p>
                        </button>
                    </nav>
                </div>
            )}

            <div className="flex flex-col md:flex-row md:h-screen">

                {/* Desktop Sidebar Navigation */}
                <div className="hidden md:block md:w-80 bg-white border-r border-gray-200 shadow-sm p-6">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Resources</h1>
                        <p className="text-gray-600">Legal documents and policies</p>
                    </div>
                    
                    <nav className="space-y-2">
                        {menuItems.map((item) => (
                            <button
                                key={item.id}
                                className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                                    selectedContent === item.id
                                        ? 'bg-blue-500 text-white shadow-md'
                                        : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600 border border-gray-200'
                                }`}
                                onClick={() => setSelectedContent(item.id)}
                            >
                                <span className="font-medium">{item.title}</span>
                                <p className="text-sm mt-1 opacity-80">{item.description}</p>
                            </button>
                        ))}

                        <button
                            className="w-full text-left p-4 rounded-xl text-gray-700 hover:bg-gray-50 hover:text-blue-600 border border-gray-200 transition-all duration-300"
                            onClick={openNewTab}
                        >
                            <span className="font-medium">FAA Privacy Policy</span>
                            <p className="text-sm mt-1 opacity-80">External FAA documentation</p>
                        </button>
                    </nav>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-white">
                    <div className="p-4 md:p-8">
                        <Suspense fallback={
                            <div className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                    <p className="text-gray-600">Loading content...</p>
                                </div>
                            </div>
                        }>
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-8">
                                {renderContent()}
                            </div>
                        </Suspense>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Resources;